import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Wrench } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WorkshopViz } from "./CinematicOS";
import { WorkshopField } from "./WorkshopField";

const GOLD = "#D4AF37";
const TEAL = "#5FD3B0";
const EASE = [0.22, 1, 0.36, 1];

// Each example runs the full Prompt → Intelligence → Build → Finished sequence.
// Field labels/types are generic nouns so the whole illustration translates cleanly.
const EXAMPLES = [
  {
    prompt: "Build an automated shift-swap flow",
    title: "Shift Swap",
    fields: [
      { label: "Requested by", type: "Staff" },
      { label: "Covering shift", type: "Date · time" },
      { label: "Approval", type: "Status" },
      { label: "Notify team", type: "Automation" },
    ],
    cols: ["Role", "Shift", "Status"],
    rows: [["Bartender", "Fri 18:00", "Approved"], ["Floor server", "Sat 12:00", "Pending"]],
  },
  {
    prompt: "Track table reservations for the week",
    title: "Reservations",
    fields: [
      { label: "Guest name", type: "Text" },
      { label: "Party size", type: "Number" },
      { label: "Time", type: "Time" },
      { label: "Table", type: "Select" },
    ],
    cols: ["Guest", "Party", "Table"],
    rows: [["Table for 4", "20:30", "Window"], ["Table for 2", "19:00", "Booth"]],
  },
  {
    prompt: "Log food waste and its cost per shift",
    title: "Waste Log",
    fields: [
      { label: "Item", type: "Text" },
      { label: "Quantity", type: "Number" },
      { label: "Reason", type: "Select" },
      { label: "Cost", type: "Currency" },
    ],
    cols: ["Item", "Qty", "Cost"],
    rows: [["Sea bass", "3", "£41"], ["Sourdough", "9", "£12"]],
  },
];

// Deliberate, unrushed timeline (ms) per phase 0..6. Phase -1 = the relocated Workshop
// illustration, held long and slow so it never feels like it rushes into the next illustration.
// Timings are intentionally generous so a visitor can read the prompt, watch the components
// assemble, and study the finished tool before it moves on.
const INTRO_DUR = 7200;
const DUR = [2000, 4200, 3400, 3000, 5200, 4600, 5200];
const STATUS = ["", "Prompt", "Interpreting", "Structuring", "Assembling", "Assembling", "Live"];

// 2×2 landing grid for the field cards inside the build frame, and where each flies in from.
const SLOTS = [
  { at: { x: -92, y: -34 }, from: { x: -300, y: -150, rz: -12 } },
  { at: { x: 92, y: -34 }, from: { x: 320, y: -120, rz: 12 } },
  { at: { x: -92, y: 40 }, from: { x: -320, y: 160, rz: 10 } },
  { at: { x: 92, y: 40 }, from: { x: 300, y: 150, rz: -10 } },
];

export const WorkshopConstructionSequence = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-15% 0px -15% 0px" });
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState(0);
  const [ex, setEx] = useState(0);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (reduced) { setPhase(6); return; }
    if (!inView) return;
    let p = -1, e = 0, timer;
    const run = () => {
      setPhase(p);
      const dur = p < 0 ? INTRO_DUR : DUR[p];
      timer = setTimeout(() => {
        p += 1;
        if (p > 6) { p = -1; e = (e + 1) % EXAMPLES.length; setEx(e); }
        run();
      }, dur);
    };
    run();
    return () => clearTimeout(timer);
  }, [inView, reduced]);

  const example = EXAMPLES[ex];
  const promptText = t(example.prompt);
  useEffect(() => {
    if (reduced) { setTyped(promptText); return; }
    if (phase < 1) { setTyped(""); return; }
    if (phase >= 2) { setTyped(promptText); return; }
    let i = 0, id;
    const step = () => {
      setTyped(promptText.slice(0, i));
      if (i < promptText.length) { i += 1; id = setTimeout(step, 40); }
    };
    step();
    return () => clearTimeout(id);
  }, [phase, ex, reduced, promptText]);

  const interpreting = phase >= 2 && phase <= 4;
  const docked = phase >= 2;                       // prompt shrinks up to a header once interpreted
  const framing = phase >= 3 && phase < 5;         // wireframe build frame visible
  const assembling = phase >= 4 && phase < 5;      // field cards land in the frame
  const finished = phase >= 5;
  const pullOut = phase >= 6;

  const stageAnim = reduced
    ? { z: 0, scale: 1, rotateX: 0 }
    : pullOut ? { z: -60, scale: 0.92, rotateX: 2 }
    : finished ? { z: 50, scale: 1.01, rotateX: 1 }
    : assembling ? { z: -20, scale: 0.98, rotateX: 5 }
    : { z: 0, scale: 1, rotateX: 3 };

  return (
    <section ref={ref} className="relative w-full overflow-hidden grain" data-testid="workshop-construction">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: `radial-gradient(1000px 540px at 50% 40%, rgba(212,175,55,0.06), transparent 70%)` }} />
        <motion.div className="absolute inset-0" animate={{ opacity: interpreting ? 0.42 : 0.12 }} transition={{ duration: 1.2, ease: EASE }}
          style={{ background: `radial-gradient(660px 360px at 50% 44%, rgba(95,211,176,0.09), transparent 72%)` }} />
        <WorkshopField count={22} />
      </div>

      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-8 pt-10 pb-20">

        <div className="h-5 mb-1" aria-live="polite">
          {STATUS[phase] && (
            <motion.span key={STATUS[phase]} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-[10.5px] font-mono-lux tracking-[0.3em] uppercase inline-flex items-center gap-2"
              style={{ color: finished ? TEAL : GOLD }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: finished ? TEAL : GOLD, boxShadow: `0 0 10px ${finished ? TEAL : GOLD}` }} />
              {t(STATUS[phase])}
            </motion.span>
          )}
        </div>

        <div className="relative w-full h-[64vh] min-h-[480px] max-h-[700px] flex items-center justify-center"
          style={{ perspective: 1600 }} data-testid="workshop-construction-stage">
          <motion.div className="relative w-full h-full flex items-center justify-center"
            style={{ transformStyle: "preserve-3d" }} animate={stageAnim} transition={{ duration: 1.6, ease: EASE }}>

            {/* INTRO — the relocated top-page Workshop illustration, held slow & deliberate before the build begins */}
            <motion.div className="absolute inset-0 flex flex-col items-center justify-center px-4" data-testid="workshop-intro"
              aria-hidden style={{ pointerEvents: "none" }}
              initial={false} animate={{ opacity: phase < 0 ? 1 : 0, scale: phase < 0 ? 1 : 0.98 }}
              transition={{ duration: 1.5, ease: EASE }}>
              <div className="w-full max-w-lg">
                <p className="text-[11px] font-mono-lux tracking-[0.3em] uppercase text-[#D4AF37] inline-flex items-center gap-2">
                  <Wrench className="w-3.5 h-3.5" /> {t("Workshop")}
                </p>
                <div className="mt-5 rounded-2xl p-5 h-48 sm:h-52 overflow-hidden relative" style={{
                  background: "linear-gradient(160deg, rgba(26,23,29,0.94), rgba(13,12,16,0.97))",
                  border: "1px solid rgba(212,175,55,0.24)",
                  boxShadow: "0 40px 80px -34px rgba(0,0,0,0.85), 0 0 44px -14px rgba(212,175,55,0.32), inset 0 1px 0 rgba(255,255,255,0.06)" }}>
                  <div aria-hidden className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}88, transparent)` }} />
                  <WorkshopViz />
                </div>
                <p className="font-serif-lux text-2xl sm:text-[2rem] leading-tight tracking-tight text-white mt-6">{t("Build what your restaurant needs — no code.")}</p>
              </div>
            </motion.div>

            {/* CONSTRUCTION — fades in slowly only after the intro has had its moment */}
            <motion.div className="absolute inset-0 flex items-center justify-center" style={{ transformStyle: "preserve-3d" }}
              initial={false} animate={{ opacity: phase >= 0 ? 1 : 0 }} transition={{ duration: 1.3, ease: EASE, delay: phase >= 0 ? 0.35 : 0 }}>

            {/* PROMPT — types in centred, then docks up into a compact header (keeps prompt→tool legible) */}
            <motion.div className="absolute left-1/2 w-[88%] max-w-[540px]" data-testid="workshop-prompt-panel"
              style={{ transformStyle: "preserve-3d", translateX: "-50%" }}
              initial={false}
              animate={phase <= 0
                ? { opacity: 0, y: 30, scale: 0.96, z: -30 }
                : docked
                  ? { opacity: 1, y: -186, scale: 0.9, z: 40, rotateX: 0 }
                  : { opacity: 1, y: 0, scale: 1, z: 90, rotateX: 6 }}
              transition={{ duration: 1.1, ease: EASE }}>
              <div className="rounded-2xl px-5 py-3.5 border" style={{
                background: "linear-gradient(180deg, rgba(21,21,25,0.82), rgba(12,12,15,0.9))",
                borderColor: interpreting ? `${GOLD}55` : "rgba(255,255,255,0.09)",
                boxShadow: interpreting ? `0 30px 70px -24px rgba(0,0,0,0.9), 0 0 30px -8px ${GOLD}55` : "0 26px 60px -26px rgba(0,0,0,0.9)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: `linear-gradient(140deg, #F6E3A6, ${GOLD})` }}>
                    <span className="w-1.5 h-1.5 rotate-45 bg-[#0A0A0B]" />
                  </span>
                  <span className="text-[9.5px] font-mono-lux tracking-[0.26em] uppercase text-white/40">{t("Workshop")}</span>
                </div>
                <div className="min-h-[24px]">
                  <span className="text-[14px] sm:text-[15px] leading-snug font-mono-lux tracking-tight"
                    style={{ color: interpreting ? GOLD : "rgba(255,255,255,0.92)", textShadow: interpreting ? `0 0 16px ${GOLD}55` : "none", transition: "color 0.6s ease, text-shadow 0.6s ease" }}>
                    {typed}
                  </span>
                  {phase === 1 && <span className="inline-block w-[2px] h-[15px] align-middle ml-0.5 animate-pulse" style={{ background: GOLD }} />}
                </div>
              </div>
            </motion.div>

            {/* connector: a thin champagne line flowing from the prompt down into the build */}
            <motion.div aria-hidden className="absolute left-1/2 -translate-x-1/2"
              style={{ top: "calc(50% - 150px)", width: 1, background: `linear-gradient(180deg, ${GOLD}, transparent)` }}
              animate={{ opacity: docked && !pullOut ? 0.6 : 0, height: docked ? 78 : 0 }}
              transition={{ duration: 0.8, ease: EASE }} />

            {/* BUILD FRAME — the structure the capability assembles into */}
            <motion.div aria-hidden className="absolute" style={{ transformStyle: "preserve-3d" }}
              animate={{ opacity: framing ? 0.9 : finished ? 0 : 0, scale: framing ? 1 : 0.96, z: framing ? 0 : -60 }}
              transition={{ duration: 0.9, ease: EASE }}>
              <div className="rounded-2xl border-2 border-dashed" style={{ width: 360, height: 200, borderColor: `${GOLD}30` }}>
                <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-0">
                  {[0, 1, 2, 3].map((k) => <div key={k} className="border border-dashed" style={{ borderColor: `${TEAL}18` }} />)}
                </div>
              </div>
            </motion.div>

            {/* FIELD CARDS — labelled components fly in and lock into the frame (intentional, detailed build) */}
            {example.fields.map((f, i) => (
              <motion.div key={i} className="absolute w-[150px]" style={{ transformStyle: "preserve-3d" }}
                initial={false}
                animate={finished
                  ? { opacity: 0, scale: 0.92, z: 60, y: SLOTS[i].at.y - 10, x: SLOTS[i].at.x }
                  : assembling
                    ? { opacity: 1, x: SLOTS[i].at.x, y: SLOTS[i].at.y, z: 30, rotateZ: 0, scale: 1 }
                    : { opacity: 0, x: SLOTS[i].from.x, y: SLOTS[i].from.y, z: -200, rotateZ: SLOTS[i].from.rz, scale: 0.78 }}
                transition={{ duration: 1.05, ease: EASE, delay: assembling && !finished ? i * 0.16 : 0 }}>
                <div className="rounded-xl px-3 py-2.5 border" style={{
                  background: "linear-gradient(180deg, rgba(25,25,29,0.9), rgba(14,14,18,0.92))",
                  borderColor: `${TEAL}33`, boxShadow: `0 18px 40px -18px rgba(0,0,0,0.85), 0 0 20px -12px ${TEAL}` }}>
                  <div className="text-[11px] text-white/85 font-mono-lux tracking-tight truncate">{t(f.label)}</div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: TEAL }} />
                    <span className="text-[9px] font-mono-lux uppercase tracking-[0.16em] text-white/40 truncate">{t(f.type)}</span>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* FINISHED CAPABILITY — the components resolve into a polished, operational tool */}
            <motion.div className="absolute left-1/2 w-[92%] max-w-[520px]" data-testid="workshop-finished-capability"
              style={{ transformStyle: "preserve-3d", translateX: "-50%" }}
              initial={false}
              animate={finished ? { opacity: 1, y: 26, z: 90, rotateX: pullOut ? 1.5 : 0, scale: 1 } : { opacity: 0, y: 40, z: 10, scale: 0.96 }}
              transition={{ duration: 1, ease: EASE }}>
              <motion.div aria-hidden className="absolute -inset-5 rounded-[28px] pointer-events-none"
                animate={finished ? { opacity: [0, 0.6, 0] } : { opacity: 0 }} transition={{ duration: 1.4, ease: "easeOut" }}
                style={{ background: `radial-gradient(closest-side, ${GOLD}3D, transparent)` }} />
              <div className="relative rounded-2xl overflow-hidden border" style={{
                background: "linear-gradient(180deg, rgba(20,20,24,0.95), rgba(12,12,15,0.97))",
                borderColor: `${GOLD}38`, boxShadow: `0 44px 96px -34px rgba(0,0,0,0.92), 0 0 40px -12px ${GOLD}38, inset 0 1px 0 rgba(255,255,255,0.05)` }}>
                {/* thin gold hairline top accent for a refined, luxury edge */}
                <div className="h-px w-full" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}66, transparent)` }} />
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(140deg, #F6E3A6, ${GOLD})` }}>
                      <span className="w-2 h-2 rotate-45 bg-[#0A0A0B]" />
                    </span>
                    <span className="font-mono-lux tracking-tight text-white text-[14px]">{t(example.title)}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[9.5px] font-mono-lux tracking-[0.18em] uppercase rounded-full px-2.5 py-1"
                    style={{ color: TEAL, background: `${TEAL}1A`, border: `1px solid ${TEAL}3D` }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: TEAL, boxShadow: `0 0 8px ${TEAL}` }} />{t("Live")}
                  </span>
                </div>
                <div className="px-4 py-3.5">
                  <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${example.cols.length}, minmax(0,1fr))` }}>
                    {example.cols.map((c) => <div key={c} className="text-[9.5px] font-mono-lux uppercase tracking-[0.16em] text-white/40 truncate">{t(c)}</div>)}
                  </div>
                  <div className="mt-2.5 space-y-1.5">
                    {example.rows.map((r, ri) => (
                      <motion.div key={ri} className="grid gap-2 rounded-lg px-2.5 py-2 bg-white/[0.035] border border-white/[0.04]"
                        style={{ gridTemplateColumns: `repeat(${example.cols.length}, minmax(0,1fr))` }}
                        initial={false} animate={finished ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                        transition={{ duration: 0.5, ease: EASE, delay: 0.4 + ri * 0.12 }}>
                        {r.map((cell, ci) => (
                          <span key={ci} className="text-[12px] text-white/80 truncate font-mono-lux tracking-tight"
                            style={ci === r.length - 1 ? { color: TEAL } : {}}>{t(cell)}</span>
                        ))}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WorkshopConstructionSequence;
